import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  Alert,
  FormControl,
  FormLabel,
  FormHelperText,
  InputAdornment,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { toastError, toastSuccess } from "src/utils/utils";
import { useTranslation } from "react-i18next";

const schema = yup.object().shape({
  oldPassword: yup.string().required("Current password is required"),
  newPassword: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("New password is required")
    .test("different-from-old", "New password must be different from current password", function(value) {
      const { oldPassword } = this.parent;
      return value !== oldPassword;
    }),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword"), null], "Passwords do not match")
    .required("Please confirm your password"),
});

const DialogChangePassword = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    setError("");

    try {
      const response = await axiosInstance.patch(ApiEndPoints.AUTH.changePassword, {
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      });

      if (response.data.status === "success") {
        toastSuccess("Password changed successfully!");
        reset();
        onClose();
        if (onSuccess) onSuccess();
      } else {
        setError(response.data.message || "Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      const errorMessage = error.response?.data?.message || "Failed to change password. Please try again.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError("");
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    onClose();
  };

  const handleToggleOldPassword = () => {
    setShowOldPassword(!showOldPassword);
  };

  const handleToggleNewPassword = () => {
    setShowNewPassword(!showNewPassword);
  };

  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const textFieldStyles = {
    backgroundColor: "#F7F7F7",
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      "& fieldset": {
        borderColor: "#EDF1F3",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#EDF1F3",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#EDF1F3",
      },
    },
    // Placeholder styling
    "& .MuiInputBase-input::placeholder": {
      color: "#777777",
      fontWeight: 500,
      fontSize: "16px",
      opacity: 1,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          padding: "8px",
        },
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#000" }}>
            Change Password
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "#666",
              "&:hover": { color: "#000" },
            }}
          >
            <HighlightOffIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {error && (
              <Alert severity="error" sx={{ borderRadius: "8px", mb: 2 }}>
                {error}
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel
                htmlFor="oldPassword"
                sx={{
                  fontSize: "12px",
                  color: "#6C7278",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                Current Password
              </FormLabel>
              <Controller
                name="oldPassword"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    onChange={onChange}
                    id="oldPassword"
                    value={value}
                    type={showOldPassword ? "text" : "password"}
                    placeholder="Enter your current password"
                    sx={textFieldStyles}
                    variant="outlined"
                    InputProps={{
                      disableUnderline: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleToggleOldPassword} edge="end">
                            {showOldPassword ?  <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              {errors.oldPassword && (
                <FormHelperText sx={{ color: "error.main" }}>
                  {errors.oldPassword.message}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel
                htmlFor="newPassword"
                sx={{
                  fontSize: "12px",
                  color: "#6C7278",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                New Password
              </FormLabel>
              <Controller
                name="newPassword"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    onChange={onChange}
                    id="newPassword"
                    value={value}
                    type={showNewPassword ? "text" : "password"}
                    placeholder="Enter your new password"
                    sx={textFieldStyles}
                    variant="outlined"
                    InputProps={{
                      disableUnderline: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleToggleNewPassword} edge="end">
                            {showNewPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              {errors.newPassword && (
                <FormHelperText sx={{ color: "error.main" }}>
                  {errors.newPassword.message}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <FormLabel
                htmlFor="confirmPassword"
                sx={{
                  fontSize: "12px",
                  color: "#6C7278",
                  fontWeight: 500,
                  mb: 1,
                }}
              >
                Confirm New Password
              </FormLabel>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextField
                    onChange={onChange}
                    id="confirmPassword"
                    value={value}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm your new password"
                    sx={textFieldStyles}
                    variant="outlined"
                    InputProps={{
                      disableUnderline: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleToggleConfirmPassword} edge="end">
                            {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
              {errors.confirmPassword && (
                <FormHelperText sx={{ color: "error.main" }}>
                  {errors.confirmPassword.message}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderColor: "#B3C4CC",
            color: "#666",
            "&:hover": {
              borderColor: "#666",
              backgroundColor: "transparent",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            px: 4,
            py: 1.5,
            backgroundColor: "#000",
            "&:hover": {
              backgroundColor: "#333",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
        >
          {loading ? "Changing..." : "Change Password"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogChangePassword;
